// src/vehicles/index.js — екран „Автопарк" (Фаза 1): табло + списък.
//
// Таблото се вижда ВЕДНАГА при отваряне: брой автомобили, изтичащи
// документи с „след N дни" и неплатени вноски, с цветен статус.

import { el, clear, $, clickableRow } from '../shared/dom.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, statCard, searchInput, loadError } from '../shared/ui.js';
import { state, cq } from '../shared/state.js';
import { vehicleForm, CATEGORY_LABELS, STATUS_LABELS } from './form.js';
import { reportsView } from './reports.js';

// Справките са ПОД-ИЗГЛЕД на Автопарка (не отделна плочка) — флагът се
// помни между рендерите, „← Към автопарка" го връща.
let _reports = false;

function daysLabel(d) {
  const n = Math.abs(d.days);
  const word = n === 1 ? 'ден' : 'дни';
  if (d.days < 0) return 'изтекъл преди ' + n + ' ' + word;
  if (d.days === 0) return 'ДНЕС';
  return 'след ' + n + ' ' + word;
}

// Сумите се събират ПО ВАЛУТИ (Каско/лизинг често са в EUR) — не се смесват.
function sumByCurrency(list) {
  const sums = {};
  list.forEach((d) => { const c = d.currency || 'EUR'; sums[c] = (sums[c] || 0) + (Number(d.amount) || 0); });
  const parts = Object.entries(sums).map(([c, v]) => money(Math.round(v * 100) / 100, c));
  return parts.length ? parts.join(' + ') : money(0, 'EUR');
}

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  if (_reports) return reportsView(page, () => { _reports = false; view(page); });

  let vehicles = [], dash = { vehicles: 0, active: 0, dues: [] };
  try {
    [vehicles, dash] = await Promise.all([
      api('/vehicles' + cq()),
      api('/vehicles/dashboard' + cq()),
    ]);
  } catch (e) { page.appendChild(loadError(() => view(page))); return; }

  const byId = new Map(vehicles.map((v) => [Number(v.id), v]));
  const openVehicle = (id) => { const v = byId.get(Number(id)); if (v) vehicleForm(v, () => view($('#page'))); };

  // ---- Таблото ----
  const unpaid = dash.dues.filter((d) => d.kind === 'installment');
  const expiring = dash.dues.filter((d) => d.kind === 'doc');
  const serviceDue = dash.service_due || [];
  page.appendChild(el('div', { class: 'cards' },
    statCard('Автомобили', String(dash.vehicles), dash.active + ' активни'),
    statCard('Изтичащи документи', String(expiring.length), 'до 30 дни (вкл. изтекли)'),
    statCard('Неплатени вноски', String(unpaid.length), sumByCurrency(unpaid)),
    statCard('Предстоящо обслужване', String(serviceDue.length), 'по дата или километри')
  ));

  if (dash.dues.length) {
    const duesPanel = el('div', { class: 'panel' },
      el('div', { class: 'panel-head' }, el('h2', { text: 'Предстоящи падежи' })));
    const tb = el('tbody');
    dash.dues.forEach((d) => {
      tb.appendChild(clickableRow(() => openVehicle(d.vehicle_id), 'Отвори автомобил ' + d.reg_number,
        el('td', {}, el('strong', { text: d.reg_number })),
        el('td', { text: d.doc_type + (d.installment_no ? ' — вноска ' + d.installment_no : '') }),
        el('td', {}, el('span', { class: 'tag ' + (d.days < 0 ? 'red' : d.days <= 7 ? 'amber' : 'gray'), text: daysLabel(d) })),
        el('td', { class: 'hide-sm' }, dfmt(d.date) + (d.effective && d.effective !== d.date ? ' (реален срок: ' + dfmt(d.effective) + ')' : '')),
        el('td', { class: 'num' }, d.amount ? money(d.amount, d.currency || 'EUR') : '—')
      ));
    });
    duesPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Кола' }), el('th', { scope: 'col', text: 'Документ' }), el('th', { scope: 'col', text: 'Срок' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', class: 'num', text: 'Сума' }))), tb));
    page.appendChild(duesPanel);
  }

  if (serviceDue.length) {
    const svcPanel = el('div', { class: 'panel' },
      el('div', { class: 'panel-head' }, el('h2', { text: 'Предстоящо обслужване' })));
    const tb = el('tbody');
    serviceDue.forEach((s) => {
      const when = [];
      if (s.next_date) when.push(dfmt(s.next_date) + (s.days != null ? (s.days < 0 ? ' (просрочено)' : ' · след ' + s.days + ' дни') : ''));
      if (s.next_odometer_km) when.push(s.next_odometer_km + ' км' + (s.km_left != null ? (s.km_left < 0 ? ' (надминато)' : ' · след ' + s.km_left + ' км') : ''));
      tb.appendChild(clickableRow(() => openVehicle(s.vehicle_id), 'Отвори автомобил ' + s.reg_number,
        el('td', {}, el('strong', { text: s.reg_number })),
        el('td', { text: s.type }),
        el('td', {}, el('span', { class: 'tag ' + (s.status === 'overdue' ? 'red' : 'amber'), text: s.status === 'overdue' ? 'просрочено' : 'наближава' })),
        el('td', { text: when.join(' · ') || '—' })
      ));
    });
    svcPanel.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Кола' }), el('th', { scope: 'col', text: 'Какво' }), el('th', { scope: 'col', text: 'Статус' }), el('th', { scope: 'col', text: 'Кога' }))), tb));
    page.appendChild(svcPanel);
  }

  // ---- Последни движения + разходите за месеца (Фаза 2) ----
  const lastWb = dash.last_waybills || [];
  const lastExp = dash.last_expenses || [];
  const monthTotals = dash.month_expenses || [];
  if (lastWb.length || lastExp.length) {
    const twoCol = el('div', { class: 'fleet-recent' });
    if (lastWb.length) {
      const tb = el('tbody');
      lastWb.forEach((w) => tb.appendChild(clickableRow(() => openVehicle(w.vehicle_id), 'Отвори автомобил ' + w.reg_number,
        el('td', {}, el('strong', { text: w.reg_number })),
        el('td', { text: '№ ' + (w.number || '') }),
        el('td', { text: dfmt(w.date_from) }),
        el('td', { class: 'num', text: (w.distance_km || 0) + ' км' }))));
      twoCol.appendChild(el('div', { class: 'panel' },
        el('div', { class: 'panel-head' }, el('h2', { text: 'Последни пътни листа' })),
        el('table', {}, el('thead', {}, el('tr', {},
          el('th', { scope: 'col', text: 'Кола' }), el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', class: 'num', text: 'Км' }))), tb)));
    }
    if (lastExp.length) {
      const tb = el('tbody');
      lastExp.forEach((x) => tb.appendChild(clickableRow(() => openVehicle(x.vehicle_id), 'Отвори автомобил ' + x.reg_number,
        el('td', {}, el('strong', { text: x.reg_number })),
        el('td', { text: x.type }),
        el('td', { text: dfmt(x.date) }),
        el('td', { class: 'num', text: money(x.amount || 0, x.currency || 'EUR') }))));
      twoCol.appendChild(el('div', { class: 'panel' },
        el('div', { class: 'panel-head' }, el('h2', { text: 'Последни разходи' }),
          el('span', { style: 'margin-left:auto;color:var(--muted);font-size:13px', text: monthTotals.length ? 'този месец: ' + monthTotals.map((t) => money(t.total, t.currency)).join(' + ') : '' })),
        el('table', {}, el('thead', {}, el('tr', {},
          el('th', { scope: 'col', text: 'Кола' }), el('th', { scope: 'col', text: 'Вид' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', class: 'num', text: 'Сума' }))), tb)));
    }
    page.appendChild(twoCol);
  }

  // ---- Списъкът ----
  const panel = el('div', { class: 'panel' });
  let q = '';
  const search = searchInput({ placeholder: 'Търси № или марка…', ariaLabel: 'Търсене на автомобил', onSearch: (v) => { q = v; drawList(); } });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Автомобили' }),
    search,
    el('button', { class: 'btn sm ghost', text: '📊 Справки', onClick: () => { _reports = true; view($('#page')); } }),
    el('button', { class: 'btn sm', text: '+ Нов автомобил', onClick: () => vehicleForm({ company_id: state().activeCompany }, () => view($('#page'))) })
  ));
  const listBox = el('div', {});
  panel.appendChild(listBox);

  function drawList() {
    clear(listBox);
    const rows = vehicles.filter((v) => !q
      || String(v.reg_number || '').toLowerCase().includes(q)
      || String(v.make || '').toLowerCase().includes(q)
      || String(v.model || '').toLowerCase().includes(q)
      || String(v.driver_name || '').toLowerCase().includes(q));
    if (!vehicles.length) { listBox.appendChild(emptyState('⛟', 'Още няма автомобили.', '+ Нов автомобил', () => vehicleForm({ company_id: state().activeCompany }, () => view($('#page'))))); return; }
    if (!rows.length) { listBox.appendChild(emptyState('⛟', 'Няма автомобил по това търсене.')); return; }
    const tb = el('tbody');
    rows.forEach((v) => {
      const dues = dash.dues.filter((d) => Number(d.vehicle_id) === Number(v.id));
      const worst = dues.some((d) => d.days < 0) ? ['проблем', 'red'] : dues.length ? ['внимание', 'amber'] : ['наред', 'green'];
      tb.appendChild(clickableRow(() => openVehicle(v.id), 'Отвори автомобил ' + v.reg_number,
        el('td', {}, el('strong', { text: v.reg_number })),
        el('td', { text: [v.make, v.model].filter(Boolean).join(' ') || '—' }),
        el('td', { class: 'hide-sm', text: CATEGORY_LABELS[v.category] || '' }),
        el('td', { class: 'hide-sm', text: v.driver_name || '—' }),
        el('td', { class: 'hide-sm num', text: v.odometer_km ? v.odometer_km + ' км' : '—' }),
        el('td', {}, el('span', { class: 'tag ' + (v.status === 'active' ? worst[1] : 'gray'), text: v.status === 'active' ? worst[0] : (STATUS_LABELS[v.status] || v.status) }))
      ));
    });
    listBox.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Рег. №' }), el('th', { scope: 'col', text: 'Марка / модел' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Категория' }),
      el('th', { scope: 'col', class: 'hide-sm', text: 'Шофьор' }), el('th', { scope: 'col', class: 'hide-sm num', text: 'Километраж' }), el('th', { scope: 'col', text: 'Статус' }))), tb));
  }
  drawList();
  page.appendChild(panel);
}
